import React, { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth-context';
import { walletAPI, userAPI } from '../lib/api';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Wallet as WalletIcon,
  ArrowUpRight,
  ArrowDownLeft,
  Plus,
  Send,
  Loader2,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';
import { format } from 'date-fns';

const WalletPage = () => {
  const { user, updateUser } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  
  // Add Balance Dialog
  const [addBalanceOpen, setAddBalanceOpen] = useState(false);
  const [addBalanceUser, setAddBalanceUser] = useState('');
  const [addBalanceAmount, setAddBalanceAmount] = useState('');
  const [addBalanceLoading, setAddBalanceLoading] = useState(false);

  // Transfer Dialog
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferUser, setTransferUser] = useState('');
  const [transferAmount, setTransferAmount] = useState('');
  const [transferLoading, setTransferLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [txnRes] = await Promise.all([
        walletAPI.getTransactions(50),
      ]);
      setTransactions(txnRes.data);

      // Fetch users if admin or distributor
      if (user?.role === 'admin') {
        const usersRes = await userAPI.getUsers();
        setUsers(usersRes.data.filter(u => u.role !== 'admin'));
      } else if (user?.role === 'distributor') {
        const usersRes = await userAPI.getUsers('retailer');
        setUsers(usersRes.data);
      }
    } catch (error) {
      console.error('Error fetching wallet data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddBalance = async () => {
    if (!addBalanceUser || !addBalanceAmount) {
      toast.error('Please fill all fields');
      return;
    }

    const amount = parseFloat(addBalanceAmount);
    if (amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setAddBalanceLoading(true);
    try {
      await walletAPI.addBalance(addBalanceUser, amount);
      toast.success('Balance added successfully');
      setAddBalanceOpen(false);
      setAddBalanceUser('');
      setAddBalanceAmount('');
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to add balance';
      toast.error(message);
    } finally {
      setAddBalanceLoading(false);
    }
  };

  const handleTransfer = async () => {
    if (!transferUser || !transferAmount) {
      toast.error('Please fill all fields');
      return;
    }

    const amount = parseFloat(transferAmount);
    if (amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (user.wallet_balance < amount) {
      toast.error('Insufficient balance');
      return;
    }

    setTransferLoading(true);
    try {
      const response = await walletAPI.transfer({
        to_user_id: transferUser,
        amount: amount,
        description: 'Balance transfer',
      });
      
      toast.success('Transfer successful');
      updateUser({ ...user, wallet_balance: response.data.sender_balance });
      setTransferOpen(false);
      setTransferUser('');
      setTransferAmount('');
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Transfer failed';
      toast.error(message);
    } finally {
      setTransferLoading(false);
    }
  };

  const totalCredit = transactions
    .filter(t => t.type === 'credit')
    .reduce((sum, t) => sum + t.amount, 0);
  
  const totalDebit = transactions
    .filter(t => t.type === 'debit')
    .reduce((sum, t) => sum + t.amount, 0);

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="wallet-page">
        {/* Wallet Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="md:col-span-1 border-0 shadow-lg text-white rounded-xl overflow-hidden" style={{ background: 'linear-gradient(135deg, #1e3a8a, #4680ff)' }}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <WalletIcon className="w-10 h-10 text-white/30" />
                {(user?.role === 'admin' || user?.role === 'distributor') && (
                  <div className="flex gap-2">
                    {user?.role === 'admin' && (
                      <Dialog open={addBalanceOpen} onOpenChange={setAddBalanceOpen}>
                        <DialogTrigger asChild>
                          <Button size="sm" variant="secondary" data-testid="add-balance-btn">
                            <Plus className="w-4 h-4 mr-1" />
                            Add
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Add Balance</DialogTitle>
                            <DialogDescription>
                              Add balance to distributor or retailer wallet
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4 py-4">
                            <div className="space-y-2">
                              <Label>Select User</Label>
                              <Select value={addBalanceUser} onValueChange={setAddBalanceUser}>
                                <SelectTrigger data-testid="add-balance-user-select">
                                  <SelectValue placeholder="Select user" />
                                </SelectTrigger>
                                <SelectContent>
                                  {users.map((u) => (
                                    <SelectItem key={u.id} value={u.id}>
                                      {u.name} ({u.role}) - ₹{u.wallet_balance?.toLocaleString()}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label>Amount (₹)</Label>
                              <Input
                                type="number"
                                placeholder="Enter amount"
                                value={addBalanceAmount}
                                onChange={(e) => setAddBalanceAmount(e.target.value)}
                                data-testid="add-balance-amount-input"
                              />
                            </div>
                          </div>
                          <DialogFooter>
                            <Button variant="outline" onClick={() => setAddBalanceOpen(false)}>
                              Cancel
                            </Button>
                            <Button
                              onClick={handleAddBalance}
                              disabled={addBalanceLoading}
                              data-testid="add-balance-submit-btn"
                            >
                              {addBalanceLoading ? <Loader2 className="w-4 h-4 spinner" /> : 'Add Balance'}
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    )}
                    
                    <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
                      <DialogTrigger asChild>
                        <Button size="sm" variant="secondary" data-testid="transfer-btn">
                          <Send className="w-4 h-4 mr-1" />
                          Transfer
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Transfer Balance</DialogTitle>
                          <DialogDescription>
                            Transfer balance to {user?.role === 'admin' ? 'users' : 'your retailers'}
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div className="space-y-2">
                            <Label>Select {user?.role === 'admin' ? 'User' : 'Retailer'}</Label>
                            <Select value={transferUser} onValueChange={setTransferUser}>
                              <SelectTrigger data-testid="transfer-user-select">
                                <SelectValue placeholder="Select recipient" />
                              </SelectTrigger>
                              <SelectContent>
                                {users.map((u) => (
                                  <SelectItem key={u.id} value={u.id}>
                                    {u.name} - ₹{u.wallet_balance?.toLocaleString()}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>Amount (₹)</Label>
                            <Input
                              type="number"
                              placeholder="Enter amount"
                              value={transferAmount}
                              onChange={(e) => setTransferAmount(e.target.value)}
                              max={user?.wallet_balance}
                              data-testid="transfer-amount-input"
                            />
                            <p className="text-sm text-muted-foreground">
                              Available: ₹{user?.wallet_balance?.toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setTransferOpen(false)}>
                            Cancel
                          </Button>
                          <Button
                            onClick={handleTransfer}
                            disabled={transferLoading}
                            data-testid="transfer-submit-btn"
                          >
                            {transferLoading ? <Loader2 className="w-4 h-4 spinner" /> : 'Transfer'}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                )}
              </div>
              <p className="text-white/50 text-xs font-semibold uppercase tracking-wider mb-1">Current Balance</p>
              <p className="text-3xl font-bold font-mono tracking-tight" data-testid="wallet-balance">
                &#8377;{user?.wallet_balance?.toLocaleString('en-IN') || '0'}
              </p>
            </CardContent>
          </Card>

          <div className="stat-accent-line green">
            <Card className="border border-slate-100 shadow-sm rounded-xl">
              <CardContent className="p-5">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.1em] mb-2">Total Credit</p>
                <p className="text-2xl font-bold text-emerald-600 font-mono">
                  +&#8377;{totalCredit.toLocaleString('en-IN')}
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="stat-accent-line orange">
            <Card className="border border-slate-100 shadow-sm rounded-xl">
              <CardContent className="p-5">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.1em] mb-2">Total Debit</p>
                <p className="text-2xl font-bold text-red-500 font-mono">
                  -&#8377;{totalDebit.toLocaleString('en-IN')}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Transactions Table */}
        <Card className="border border-slate-100 shadow-sm rounded-xl overflow-hidden">
          <CardHeader className="pb-3 pt-5 px-6">
            <CardTitle className="text-base font-semibold text-slate-900">Transaction History</CardTitle>
            <CardDescription className="text-xs text-slate-400">Your recent wallet transactions</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <WalletIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No transactions yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Balance</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.map((txn) => (
                      <TableRow key={txn.id}>
                        <TableCell>
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            txn.type === 'credit' ? 'bg-green-100' : 'bg-red-100'
                          }`}>
                            {txn.type === 'credit' ? (
                              <ArrowDownLeft className="w-4 h-4 text-green-600" />
                            ) : (
                              <ArrowUpRight className="w-4 h-4 text-red-600" />
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="max-w-xs truncate">
                          {txn.description}
                        </TableCell>
                        <TableCell>
                          <span className={txn.type === 'credit' ? 'text-green-600' : 'text-red-600'}>
                            {txn.type === 'credit' ? '+' : '-'}₹{txn.amount.toLocaleString()}
                          </span>
                        </TableCell>
                        <TableCell>₹{txn.balance_after.toLocaleString()}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {format(new Date(txn.created_at), 'dd MMM yyyy, hh:mm a')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default WalletPage;
